/**
 * @fileoverview Lazy Loading with Retry - Resilient code-splitting utility
 * 
 * @description
 * Wraps React.lazy() with exponential backoff retry logic to handle
 * chunk loading failures gracefully. Essential for production SPAs
 * where network issues or stale caches can break code-splitting.
 * 
 * ## Retry Strategy
 * ```
 * Attempt 1: Immediate
 * Attempt 2: Wait 1000ms (1s)
 * Attempt 3: Wait 2000ms (2s)
 * Attempt 4: Wait 4000ms (4s) [final]
 *           ↓
 * If all fail: Mark as stale, throw error
 * ```
 * 
 * ## Integration with Cache Busting
 * ```
 * lazyWithRetry → fails → sets sessionStorage flag
 *                              ↓
 *                    VersionChecker detects
 *                              ↓
 *                    Prompts user to refresh
 * ```
 * 
 * ## Error Detection Patterns
 * - "Failed to fetch dynamically imported module"
 * - "Loading chunk" errors
 * - "Importing a module script failed"
 * 
 * @example
 * ```tsx
 * // Instead of React.lazy()
 * const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));
 * 
 * // With custom retry count
 * const HeavyChart = lazyWithRetry(
 *   () => import('./components/HeavyChart'),
 *   5 // 5 retries for important components
 * );
 * ```
 * 
 * @module lib/lazyWithRetry
 * @see VersionChecker - Companion component for stale detection
 * @see serviceWorkerManager - Full cache invalidation
 */

import { lazy, ComponentType } from 'react';

/** Delay between retry attempts in milliseconds */
const RETRY_DELAY_MS = 1000;

/** Default maximum retry attempts */
const MAX_RETRIES = 3;

/**
 * Wraps React.lazy() with exponential backoff retry logic
 * 
 * @template T - Component type (must extend ComponentType)
 * @param {() => Promise<{default: T}>} importFn - Dynamic import function
 * @param {number} [retries=3] - Maximum retry attempts
 * @returns {React.LazyExoticComponent<T>} Lazy component with retry logic
 * 
 * @example
 * ```tsx
 * // Basic usage
 * const MyPage = lazyWithRetry(() => import('./pages/MyPage'));
 * 
 * // In routes
 * <Route path="/my-page" element={
 *   <Suspense fallback={<PageLoader />}>
 *     <MyPage />
 *   </Suspense>
 * } />
 * ```
 * 
 * @throws {Error} After all retries exhausted, throws the last error
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  retries = MAX_RETRIES
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    let lastError: Error | null = null;
    
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        // On retry, wait with exponential backoff
        if (attempt > 0) {
          await new Promise(resolve => 
            setTimeout(resolve, RETRY_DELAY_MS * Math.pow(2, attempt - 1))
          );
          
          console.warn(`[lazyWithRetry] Retry attempt ${attempt}/${retries}`);
        }
        
        return await importFn();
      } catch (error) {
        lastError = error as Error;
        
        // Only retry on network/chunk errors, not syntax errors
        const isChunkError = 
          lastError.message?.includes('Failed to fetch') ||
          lastError.message?.includes('Loading chunk') ||
          lastError.message?.includes('dynamically imported module') ||
          lastError.message?.includes('Importing a module script failed');
        
        if (!isChunkError || attempt === retries) {
          // On final failure for chunk errors, auto-reload to get fresh assets
          if (isChunkError && attempt === retries) {
            console.error('[lazyWithRetry] All retries exhausted - auto-reloading');
            
            // Circuit breaker: prevent infinite reload loop
            const reloadCount = parseInt(sessionStorage.getItem('chunk_reload_count') || '0', 10);
            if (reloadCount < 2) {
              sessionStorage.setItem('chunk_reload_count', String(reloadCount + 1));
              // Clear after 30 seconds to allow future reloads
              setTimeout(() => sessionStorage.removeItem('chunk_reload_count'), 30000);
              window.location.reload();
              // Return a never-resolving promise to prevent the error from propagating
              return new Promise(() => {});
            }
            // If we've reloaded twice already, let error propagate to ErrorBoundary
            sessionStorage.setItem('stale_chunks_detected', 'true');
          }
          throw lastError;
        }
      }
    }
    
    throw lastError;
  });
}
